import { useState } from 'react'
import {
  Space,
  Typography,
  Tooltip,
  Row,
  Col,
  Form,
  Modal as ModalAntd,
  notification,
  Radio,
} from 'antd'
import { SettingOutlined, DeleteOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { useAsync } from 'react-use'
import _ from 'lodash'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Input from 'components/Input'
import TextArea from 'components/TextArea'
import Select from 'components/Select'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import {
  getReportTypeListApi,
  createReportTypeApi,
  deleteReportTypeByIdApi,
  updateReportTypeByIdApi,
} from 'services/reportType'
import { getOrganizationListApi } from 'services/organization'

import TableOrganizationWithData from './components/TableOrganizationWithData'

import Styled from './styled'

const DEFAULT_PAGE_SIZE = 10

export default function PageReportType () {
  const { t } = useTranslate()

  const [form1] = Form.useForm()
  const [form2] = Form.useForm()
  const [form3] = Form.useForm()

  const [dataSource, setDataSource] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    param: null,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all') // all, active, inactive
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [createModalPage, setCreateModalPage] = useState(1) // 1, 2, 3
  const [organizations, setOrganizations] = useState([])
  const [organizationsLoading, setOrganizationsLoading] = useState(false)

  const getOrganizations = async () => {
    if (organizationsLoading) return

    setOrganizationsLoading(true)

    const res = await getOrganizationListApi({ is_active: true })

    setOrganizationsLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setOrganizations(res.data)
  }

  useAsync(async () => {
    getOrganizations()
  }, [])

  const _list = async () => {
    if (loading) return

    const params = {
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getReportTypeListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setTotal(res.data?.total)
    setDataSource(res.data?.items)
  }

  const _create = async (values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await createReportTypeApi({
      ...values,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form1.resetFields()
    form2.resetFields()
    form3.resetFields()
  }

  const _update = async (id, values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await updateReportTypeByIdApi({
      id,
      ...values,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form1.resetFields()
    form2.resetFields()
    form3.resetFields()
  }

  const _delete = async (id) => {
    if (deleting) return

    setDeleting(true)

    const res = await deleteReportTypeByIdApi(id)

    setDeleting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()
  }

  useAsync(async () => {
    _list()
  }, [status, filters])

  const getReporterOrReceiverToEditForm = ({ editingDataSource, type }) => {
    let result = {}

    if (!_.isEmpty(editingDataSource?.companies)) {
      _.forEach(editingDataSource?.companies, (item) => {
        if (item?.[type]) {
          _.forEach(organizations, (organization) => {
            if (organization.id === item.company_id) {
              result[organization.key] = {
                id: organization.id,
                level: 'company',
                record: organization,
                selected: true,
              }
            }
          })
        }
      })
    }

    if (!_.isEmpty(editingDataSource?.groups)) {
      _.forEach(editingDataSource?.groups, (item) => {
        if (item?.[type]) {
          _.forEach(organizations, (organization) => {
            _.forEach(organization.groups, (group) => {
              if (group.id === item.group_id) {
                result[group.key] = {
                  id: group.id,
                  level: 'group',
                  record: group,
                  selected: true,
                }
              }
            })
          })
        }
      })
    }

    if (!_.isEmpty(editingDataSource?.bus)) {
      _.forEach(editingDataSource?.bus, (item) => {
        if (item?.[type]) {
          _.forEach(organizations, (organization) => {
            _.forEach(organization?.groups, (group) => {
              _.forEach(group?.bus, (bu) => {
                if (bu.id === item.bu_id) {
                  result[bu.key] = {
                    id: bu.id,
                    level: 'bu',
                    record: bu,
                    selected: true,
                  }
                }
              })
            })
          })
        }
      })
    }

    if (!_.isEmpty(editingDataSource?.divisions)) {
      _.forEach(editingDataSource?.divisions, (item) => {
        if (item?.[type]) {
          _.forEach(organizations, (organization) => {
            _.forEach(organization?.groups, (group) => {
              _.forEach(group?.bus, (bu) => {
                _.forEach(bu?.divisions, (division) => {
                  if (division.id === item.division_id) {
                    result[division.key] = {
                      id: division.id,
                      level: 'division',
                      record: division,
                      selected: true,
                    }
                  }
                })
              })
            })
          })
        }
      })
    }

    if (!_.isEmpty(editingDataSource?.departments)) {
      _.forEach(editingDataSource?.departments, (item) => {
        if (item?.[type]) {
          _.forEach(organizations, (organization) => {
            _.forEach(organization?.groups, (group) => {
              _.forEach(group?.bus, (bu) => {
                _.forEach(bu?.divisions, (division) => {
                  _.forEach(division?.departments, (department) => {
                    if (department.id === item.department_id) {
                      result[department.key] = {
                        id: department.id,
                        level: 'department',
                        record: department,
                        selected: true,
                      }
                    }
                  })
                })
              })
            })
          })
        }
      })
    }

    if (!_.isEmpty(editingDataSource?.sections)) {
      _.forEach(editingDataSource?.sections, (item) => {
        if (item?.[type]) {
          _.forEach(organizations, (organization) => {
            _.forEach(organization?.groups, (group) => {
              _.forEach(group?.bus, (bu) => {
                _.forEach(bu?.divisions, (division) => {
                  _.forEach(division?.departments, (department) => {
                    _.forEach(department?.sections, (section) => {
                      if (section.id === item.section_id) {
                        result[section.key] = {
                          id: section.id,
                          level: 'section',
                          record: section,
                          selected: true,
                        }
                      }
                    })
                  })
                })
              })
            })
          })
        }
      })
    }

    return result
  }

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      className: 'bold',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: t('Reporter'),
      dataIndex: 'reporter',
      key: 'reporter',
      sorter: (a, b) => {
        const reporterCompaniesA = _.filter(a?.companies, { is_reporter: true })
        const reporterCompaniesNameA = _.map(
          reporterCompaniesA,
          (reporterCompany) => reporterCompany?.company_name
        )
        const reporterCompaniesB = _.filter(b?.companies, { is_reporter: true })
        const reporterCompaniesNameB = _.map(
          reporterCompaniesB,
          (reporterCompany) => reporterCompany?.company_name
        )
        const reporterCompaniesNameAA = a?.is_allreporter
          ? 'All'
          : _.join(reporterCompaniesNameA, ', ')
        const reporterCompaniesNameBB = b?.is_allreporter
          ? 'All'
          : _.join(reporterCompaniesNameB, ', ')

        return reporterCompaniesNameAA.localeCompare(reporterCompaniesNameBB)
      },
      render: (text, record, index) => {
        const reporterCompanies = _.filter(record?.companies, { is_reporter: true })
        const reporterDepartments = _.filter(record?.departments, { is_reporter: true })
        const reporterCompaniesName = _.map(
          reporterCompanies,
          (reporterCompany) => reporterCompany?.company_name
        )
        const reporterDepartmentsName = _.map(
          reporterDepartments,
          (reporterDepartment) => reporterDepartment?.department_name
        )
        return (
          <Space direction='vertical' size={0}>
            <Typography.Text>
              Company: {record?.is_allreporter ? 'All' : _.join(reporterCompaniesName, ', ')}
            </Typography.Text>
            <Typography.Text>
              Department: {record?.is_allreporter ? 'All' : _.join(reporterDepartmentsName, ', ')}
            </Typography.Text>
          </Space>
        )
      },
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      sorter: (a, b) => a.status.label.localeCompare(b.status.label),
      render: (text, record, index) => {
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.isEnabled,
              'is-inactive': !record.isEnabled,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Tooltip title={t('Edit')}>
              <Button
                type='link'
                icon={<SettingOutlined />}
                onClick={() => {
                  setEditingId(record?.id)

                  const editingDataSource = _.find(dataSource, { id: record?.id })

                  const form1Data = {
                    name: editingDataSource.name,
                    description: editingDataSource.description,
                    isEnabled: editingDataSource.isEnabled,
                    riskOwnerIs: editingDataSource.riskOwnerIs,
                    reportConfidential: editingDataSource.reportConfidential,
                    setupReporter: editingDataSource.setupReporter,
                  }

                  const form2Data = {
                    setupReceiver: editingDataSource.setupReceiver,
                    riskTeamReporter: editingDataSource.riskTeamReporter,
                  }

                  if (!form1Data.setupReporter) {
                    form2Data.reporter = getReporterOrReceiverToEditForm({
                      editingDataSource,
                      type: 'is_reporter',
                    })
                  }

                  const form3Data = {
                    riskTeamReporter: editingDataSource.riskTeamReporter,
                  }

                  if (!form2Data.setupReceiver) {
                    form3Data.receiver = getReporterOrReceiverToEditForm({
                      editingDataSource,
                      type: 'is_analyst',
                    })
                  }

                  form1.setFieldsValue(form1Data)
                  form2.setFieldsValue(form2Data)
                  form3.setFieldsValue(form3Data)

                  setShowCreateModal(true)
                }}
              />
            </Tooltip>
            <Tooltip title={t('Delete')}>
              <Button
                type='link'
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  const deletingDataSource = _.find(dataSource, { id: record?.id })

                  ModalAntd.confirm({
                    title: 'Confirm deletion: Report Type',
                    icon: null,
                    maskClosable: true,
                    closeIcon: true,
                    content: (
                      <div>
                        <dl>
                          <dt>Report Type Name</dt>
                          <dd>- {deletingDataSource.name}</dd>
                          <dt>Report Type Description</dt>
                          <dd>- {deletingDataSource.description}</dd>
                        </dl>
                      </div>
                    ),
                    okButtonProps: {
                      shape: 'round',
                    },
                    okText: 'Delete',
                    okType: 'primary',
                    cancelButtonProps: {
                      shape: 'round',
                    },
                    cancelText: 'Cancel',
                    onOk: async () => {
                      await _delete(record?.id)
                    },
                  })
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  const modalFormSubmit = () => {
    const form1Values = form1.getFieldsValue()
    const form2Values = form2.getFieldsValue()
    const form3Values = form3.getFieldsValue()

    const organizationGroups = {
      company: [],
      group: [],
      business_unit: [],
      division: [],
      department: [],
      section: [],
    }

    // Define all to fase
    // _.forEach(organizations, (organization) => {
    //   organizationGroups.company.push({
    //     id: organization.id,
    //     is_reporter: false,
    //     is_analyst: false,
    //   })
    //   _.forEach(organization?.groups, (group) => {
    //     organizationGroups.group.push({
    //       id: group.id,
    //       is_reporter: false,
    //       is_analyst: false,
    //     })
    //     _.forEach(group?.bus, (bu) => {
    //       organizationGroups.business_unit.push({
    //         id: bu.id,
    //         is_reporter: false,
    //         is_analyst: false,
    //       })
    //       _.forEach(bu?.divisions, (division) => {
    //         organizationGroups.division.push({
    //           id: division.id,
    //           is_reporter: false,
    //           is_analyst: false,
    //         })
    //         _.forEach(division?.departments, (department) => {
    //           organizationGroups.department.push({
    //             id: department.id,
    //             is_reporter: false,
    //             is_analyst: false,
    //           })
    //           _.forEach(department?.sections, (section) => {
    //             organizationGroups.section.push({
    //               id: section.id,
    //               is_reporter: false,
    //               is_analyst: false,
    //             })
    //           })
    //         })
    //       })
    //     })
    //   })
    // })

    const setOrganizations = ({ selectedOrganizations, type }) => {
      _.forEach(selectedOrganizations, (o) => {
        const id = o.id
        const selected = o.selected

        let fieldData = []
        let fieldName = ''

        if (o.level === 'company') {
          fieldData = organizationGroups.company
          fieldName = 'company'
        }

        if (o.level === 'group') {
          fieldData = organizationGroups.group
          fieldName = 'group'
        }

        if (o.level === 'bu') {
          fieldData = organizationGroups.business_unit
          fieldName = 'business_unit'
        }

        if (o.level === 'division') {
          fieldData = organizationGroups.division
          fieldName = 'division'
        }

        if (o.level === 'department') {
          fieldData = organizationGroups.department
          fieldName = 'department'
        }

        if (o.level === 'section') {
          fieldData = organizationGroups.section
          fieldName = 'section'
        }

        const fieldIndex = _.findIndex(fieldData, { id })
        if (fieldIndex === -1) {
          organizationGroups[fieldName].push({
            id,
            is_reporter: false,
            is_analyst: false,
            [type]: selected,
          })
        } else {
          organizationGroups[fieldName][fieldIndex][type] = selected
        }
      })
    }

    // All Reporter
    if (form1Values?.setupReporter) {
      _.forEach(organizations, (organization) => {
        const id = organization.id
        const fieldData = organizationGroups.company
        const fieldName = 'company'
        const fieldIndex = _.findIndex(fieldData, { id })
        const selected = true
        if (fieldIndex === -1) {
          organizationGroups[fieldName].push({
            id,
            is_reporter: selected,
            is_analyst: false,
          })
        } else {
          organizationGroups[fieldName][fieldIndex].is_reporter = selected
        }
      })
    } else {
      setOrganizations({ selectedOrganizations: form2Values?.reporter, type: 'is_reporter' })
    }

    // To Risk Team
    if (form2Values?.setupReceiver) {
      // Hard Code
      const division41Index = _.findIndex(organizationGroups.division, { id: 41 })
      if (division41Index === -1) {
        organizationGroups.division.push({
          id: 41,
          is_reporter: false,
          is_analyst: true,
        })
      } else {
        organizationGroups.division[division41Index].is_analyst = true
      }
    } else {
      setOrganizations({ selectedOrganizations: form3Values?.receiver, type: 'is_analyst' })
    }

    // Uniq By ID
    organizationGroups.company = _.uniqBy(organizationGroups.company, 'id')
    organizationGroups.group = _.uniqBy(organizationGroups.group, 'id')
    organizationGroups.business_unit = _.uniqBy(organizationGroups.business_unit, 'id')
    organizationGroups.division = _.uniqBy(organizationGroups.division, 'id')
    organizationGroups.department = _.uniqBy(organizationGroups.department, 'id')
    organizationGroups.section = _.uniqBy(organizationGroups.section, 'id')

    const values = {
      report_type_name: form1Values?.name,
      report_type_description: form1Values?.description,
      is_active: form1Values?.isEnabled,
      is_report_ownself: form1Values?.riskOwnerIs,
      is_confidential: form1Values?.reportConfidential,
      is_skiptoapprove: null,
      is_allreporter: form1Values?.setupReporter,
      is_rmreceiver: form2Values?.setupReceiver,
      ...organizationGroups,
    }

    if (form2Values?.setupReceiver) {
      values.is_skiptoapprove = form2Values?.riskTeamReporter
    } else {
      values.is_skiptoapprove = form3Values?.riskTeamReporter
    }

    if (editingId) {
      _update(editingId, values)
    } else {
      _create(values)
    }
  }

  return (
    <Styled className='page page-report-type'>
      <Page
        title={t('Report Type')}
        searchPlaceholder={t('Search Report Type or description')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between'>
            <Col></Col>
            <Col>
              <Button type='primary' size='large' onClick={() => setShowCreateModal(true)}>
                {t('Create New Report Type')}
              </Button>
            </Col>
          </Row>
          <Card title={t('Report Type')}>
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              loading={loading}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => t('Total ${total} items', { total }),
              }}
              onChange={(pagination, filters) => {
                setFilters({
                  ...filters,
                  page: pagination.current,
                  size: pagination.pageSize,
                })
              }}
              scroll={{ x: 800 }}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        className='report-type-modal'
        width={1000}
        visible={showCreateModal}
        title={editingId ? t('Update Report Type') : t('Create New Report Type')}
        onCancel={() => {
          setShowCreateModal(false)
        }}
        afterClose={() => {
          if (editingId) {
            form1.resetFields()
            form2.resetFields()
            form3.resetFields()
          }

          setEditingId(null)
          setCreateModalPage(1)
        }}
        footer={null}
      >
        <div>
          <Form
            hidden={createModalPage !== 1}
            name='create-modal-page-1'
            form={form1}
            layout='vertical'
            initialValues={{
              isEnabled: true,
              riskOwnerIs: true,
              reportConfidential: true,
              setupReporter: true,
            }}
            onFinish={(values) => {
              setCreateModalPage(createModalPage + 1)
            }}
          >
            <Form.Item label={t('Report Type Name')} name='name' rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item
              label={t('Report Type Description')}
              name='description'
              rules={[{ required: true }]}
            >
              <TextArea />
            </Form.Item>

            <Row justify='space-between'>
              <Col>
                <Form.Item name='isEnabled' label='Status'>
                  <Select
                    options={[
                      { label: t('Active'), value: true },
                      { label: t('Inactive'), value: false },
                    ]}
                    style={{ width: 200, textAlign: 'center' }}
                  />
                </Form.Item>
              </Col>
              <Col></Col>
            </Row>

            <Form.Item name='riskOwnerIs' label='Risk Owner is'>
              <Radio.Group>
                <Radio value={true}>Report themselves</Radio>
                <Radio value={false}>Report Another</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name='reportConfidential' label='Report Confidential'>
              <Radio.Group>
                <Radio value={false}>Public</Radio>
                <Radio value={true}>Confidential (Hide for Risk Owner)</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name='setupReporter' label='Setup Reporter' className='setup-reporter'>
              <Radio.Group>
                <Space direction='vertical'>
                  <Radio value={true}>Make report for all Company</Radio>
                  <Radio value={false}>Select specific Company and/or Department Reporter</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            <Row justify='space-between'>
              <Col></Col>
              <Col>
                <Button
                  type='primary'
                  htmlType='button'
                  onClick={() => {
                    form1.submit()
                  }}
                >
                  Next
                </Button>
              </Col>
            </Row>
          </Form>

          <Form
            hidden={createModalPage !== 2}
            name='create-modal-page-2'
            form={form2}
            layout='vertical'
            initialValues={{
              riskTeamReporter: true,
            }}
            onFinish={async (values) => {
              if (!values.setupReceiver) {
                setCreateModalPage(createModalPage + 1)
              } else {
                modalFormSubmit()
              }
            }}
          >
            <Form.Item label='Report Type Name'>
              <Typography.Text strong>{form1.getFieldValue('name')}</Typography.Text>
            </Form.Item>

            {form1.getFieldValue('setupReporter') === false && (
              <Form.Item name='reporter' label='Reporter'>
                <TableOrganizationWithData
                  loading={organizationsLoading}
                  dataSource={organizations}
                />
              </Form.Item>
            )}

            <Form.Item
              name='setupReceiver'
              label='Setup Receiver'
              className='setup-receiver'
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space direction='vertical'>
                  <Radio value={true}>To Risk Team</Radio>
                  <Radio value={false}>Select specific Company and/or Department Receiver</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, curValues) =>
                prevValues.setupReceiver !== curValues.setupReceiver
              }
            >
              {({ getFieldValue }) => {
                const currentSetupReceiver = getFieldValue('setupReceiver')

                if (!currentSetupReceiver) {
                  return null
                }

                return (
                  <Form.Item name='riskTeamReporter' label='Risk Team Reporter'>
                    <Radio.Group>
                      <Radio value={false}>To Risk Co</Radio>
                      <Radio value={true}>To Risk Approver</Radio>
                    </Radio.Group>
                  </Form.Item>
                )
              }}
            </Form.Item>

            <Row justify='space-between'>
              <Col>
                <Button
                  htmlType='button'
                  onClick={() => {
                    setCreateModalPage(createModalPage - 1)
                  }}
                >
                  Previous
                </Button>
              </Col>
              <Col>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, curValues) =>
                    prevValues.setupReceiver !== curValues.setupReceiver
                  }
                >
                  {({ getFieldValue }) => {
                    const currentSetupReceiver = getFieldValue('setupReceiver')

                    return (
                      <Button
                        type='primary'
                        htmlType='button'
                        onClick={() => {
                          form2.submit()
                        }}
                      >
                        {currentSetupReceiver ? (editingId ? 'Update' : 'Create') : 'Next'}
                      </Button>
                    )
                  }}
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Form
            hidden={createModalPage !== 3}
            name='create-modal-page-3'
            form={form3}
            layout='vertical'
            initialValues={{
              riskTeamReporter: true,
            }}
            onFinish={() => {
              modalFormSubmit()
            }}
          >
            <Form.Item label='Report Type Name'>
              <Typography.Text strong>{form1.getFieldValue('name')}</Typography.Text>
            </Form.Item>

            <Form.Item name='receiver' label='Receiver'>
              <TableOrganizationWithData
                loading={organizationsLoading}
                dataSource={organizations}
              />
            </Form.Item>

            <Form.Item name='riskTeamReporter' label='Risk Team Reporter'>
              <Radio.Group>
                <Radio value={false}>To Risk Co</Radio>
                <Radio value={true}>To Risk Approver</Radio>
              </Radio.Group>
            </Form.Item>

            <Row justify='space-between'>
              <Col>
                <Button
                  htmlType='button'
                  onClick={() => {
                    setCreateModalPage(createModalPage - 1)
                  }}
                >
                  Previous
                </Button>
              </Col>
              <Col>
                <Button
                  type='primary'
                  htmlType='button'
                  onClick={() => {
                    form3.submit()
                  }}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </Styled>
  )
}
